import React, { useState, useEffect } from 'react';
import { X, Mail, Lock, User, Phone, Sparkles, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import ForgotPasswordModal from './ForgotPasswordModal';
import { fbTrackRegistration } from '../lib/fbpixel';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [step, setStep] = useState('email'); // 'email', 'login', 'signup'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailCheckResult, setEmailCheckResult] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('email');
      setFormData({ name: '', email: '', phone: '', password: '' });
      setError('');
      setEmailCheckResult(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Check if email exists when user enters email
  const checkEmailExists = async (email) => {
    if (!email || !email.includes('@')) return;
    
    setCheckingEmail(true);
    try {
      const response = await axios.get(`${API}/auth/check-email/${encodeURIComponent(email)}`);
      setEmailCheckResult(response.data);
      
      if (response.data.exists) {
        setStep('login');
        if (response.data.name) {
          setFormData(prev => ({ ...prev, name: response.data.name }));
        }
      } else {
        setStep('signup');
      }
    } catch (err) {
      console.error('Email check error:', err);
      setStep('signup'); // Default to signup if check fails
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleEmailContinue = () => {
    if (!formData.email || !formData.email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }
    setError('');
    checkEmailExists(formData.email);
  };

  const handleGoogleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    setLoading(true);
    setError('');
    try {
      const redirectUrl = window.location.origin + '/dashboard';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (err) {
      setError('Could not start Google sign-in. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (step === 'signup' && (!formData.name || !formData.phone)) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (!formData.email || !formData.password) {
      setError('Email and Password are required');
      setLoading(false);
      return;
    }

    try {
      const endpoint = step === 'signup' ? '/auth/signup' : '/auth/login';
      const response = await axios.post(`${API}${endpoint}`, formData);

      // FB Pixel: track new registrations
      if (step === 'signup') {
        try {
          fbTrackRegistration({ method: 'email' });
        } catch (_e) { /* silent */ }
      }

      localStorage.setItem('user', JSON.stringify(response.data.user));
      onLoginSuccess(response.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const getStepTitle = () => {
    if (step === 'email') return 'Welcome to DropSquadz';
    if (step === 'login') return `Welcome back${emailCheckResult?.name ? `, ${emailCheckResult.name}` : ''}!`;
    return 'Create Account';
  };

  return (
    <>
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-md p-4 animate-in fade-in">
        <Card className="w-full max-w-md bg-white border-2 border-blue-500/30 shadow-2xl shadow-blue-500/20 animate-in slide-in-from-bottom">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src="/dropsquadz-logo.jpeg"
                  alt="DropSquadz"
                  className="w-11 h-11 rounded-full ring-2 ring-blue-500/40 object-cover flex-shrink-0"
                  data-testid="login-modal-logo"
                />
                <CardTitle className="text-xl sm:text-2xl text-blue-600 min-w-0 truncate">
                  {getStepTitle()}
                </CardTitle>
              </div>
              <button 
                onClick={onClose} 
                aria-label="Close"
                className="text-slate-400 hover:text-slate-600 transition-all hover:rotate-90 hover:scale-110 flex-shrink-0"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            {step !== 'email' && (
              <p className="text-sm text-slate-500 mt-1">
                {step === 'login' ? 'Enter your password to login' : 'Complete your profile to get started'}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border-2 border-red-200 rounded-lg text-red-600 text-sm animate-in slide-in-from-top flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                {error}
              </div>
            )}

            <Button
              onClick={handleGoogleAuth}
              disabled={loading}
              className="w-full bg-white hover:bg-slate-50 text-slate-800 font-semibold shadow-lg border-2 border-slate-200 hover:border-blue-300 transition-all hover:scale-105"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-400">Or continue with email</span>
              </div>
            </div>

            {step === 'email' ? (
              /* Email Entry Step */
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 flex items-center font-medium">
                    <Mail className="w-4 h-4 mr-2 text-blue-500" />
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    onKeyPress={(e) => e.key === 'Enter' && handleEmailContinue()}
                    className="bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20"
                    placeholder="your@email.com"
                    autoFocus
                  />
                </div>
                <Button
                  onClick={handleEmailContinue}
                  disabled={checkingEmail || !formData.email}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30"
                >
                  {checkingEmail ? (
                    <span className="flex items-center">
                      <div className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Checking...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      Continue
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </span>
                  )}
                </Button>
              </div>
            ) : (
              /* Login or Signup Form */
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Show email (read-only in subsequent steps) */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 flex items-center font-medium">
                    <Mail className="w-4 h-4 mr-2 text-blue-500" />
                    Email
                    {emailCheckResult?.exists && (
                      <CheckCircle className="w-4 h-4 ml-2 text-green-500" />
                    )}
                  </Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      readOnly
                      className="bg-slate-100 border-slate-200 text-slate-600 flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setStep('email');
                        setEmailCheckResult(null);
                      }}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      Change
                    </Button>
                  </div>
                </div>

                {step === 'signup' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-700 flex items-center font-medium">
                        <User className="w-4 h-4 mr-2 text-blue-500" />
                        Full Name
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500"
                        placeholder="Your full name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-700 flex items-center font-medium">
                        <Phone className="w-4 h-4 mr-2 text-green-500" />
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-green-500"
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700 flex items-center font-medium">
                    <Lock className="w-4 h-4 mr-2 text-blue-500" />
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500"
                    placeholder={step === 'signup' ? 'Create a password' : 'Enter your password'}
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30 transition-all hover:scale-105"
                >
                  {loading ? (
                    <span className="flex items-center">
                      <div className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : step === 'login' ? (
                    <span className="flex items-center">
                      <Lock className="w-4 h-4 mr-2" />
                      Sign In
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Create Account
                    </span>
                  )}
                </Button>

                {step === 'login' && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium underline-offset-2 hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                {/* Toggle option */}
                <p className="text-center text-sm text-slate-500">
                  {step === 'login' ? (
                    <>
                      New to DropSquadz?{' '}
                      <button
                        type="button"
                        onClick={() => setStep('signup')}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Create account
                      </button>
                    </>
                  ) : (
                    <>
                      Already have an account?{' '}
                      <button
                        type="button"
                        onClick={() => setStep('login')}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Sign in
                      </button>
                    </>
                  )}
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        defaultEmail={formData.email}
      />
    </>
  );
};

export default LoginModal;
